/**
 * The year-grid schedule view (prototype image9): one sheet per year, a row per
 * contractor, twelve month columns, the activities as dots on their planned dates, the
 * shutdown/Ramadan calendar periods as column overlays, the quarterly rollup footer and
 * the read-only signature block.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Everything here is computed from data the schedule screen already fetched: the
 * activities and the calendar periods. Nothing is invented: a contractor with no
 * activities in the year still gets its row, and a signature nobody has recorded yet
 * says so. Clicking a dot opens the activity detail (the prototype's tap-to-edit).
 */

import { Link } from "react-router";

import type { RfiSummary } from "../api/rfis";
import type { Activity, CalendarPeriod, TransitionEntry } from "../api/schedules";
import { strings, term } from "../brand/strings";
import { activityAttention, rfiAttention } from "../calendar/model";

/** The five rollup statuses, in the prototype footer's own order. */
export const ROLLUP_STATUSES = [
  "PLANNED",
  "CONDUCTED",
  "CANCELLED",
  "FOLLOW_UP",
  "RESCHEDULED",
] as const;

export type RollupStatus = (typeof ROLLUP_STATUSES)[number];

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const ROLLUP_LABELS: Record<RollupStatus, string> = {
  PLANNED: strings.schedules.rollupPlanned,
  CONDUCTED: strings.schedules.rollupConducted,
  CANCELLED: strings.schedules.rollupCancelled,
  FOLLOW_UP: strings.schedules.rollupFollowUp,
  RESCHEDULED: strings.schedules.rollupRescheduled,
};

/** Per quarter, the five counts. An activity is a quarter's by its planned date. */
export function quarterCounts(
  activities: Activity[],
): Record<number, Record<RollupStatus, number>> {
  const quarters: Record<number, Record<RollupStatus, number>> = {};
  for (const quarter of [1, 2, 3, 4]) {
    quarters[quarter] = {
      PLANNED: 0,
      CONDUCTED: 0,
      CANCELLED: 0,
      FOLLOW_UP: 0,
      RESCHEDULED: 0,
    };
  }
  for (const activity of activities) {
    const month = Number(activity.plannedDate.slice(5, 7));
    if (!Number.isInteger(month) || month < 1 || month > 12) {
      continue;
    }
    const bucket = quarters[Math.ceil(month / 3)];
    const status = activity.status as RollupStatus;
    if (bucket !== undefined && (ROLLUP_STATUSES as readonly string[]).includes(status)) {
      bucket[status] += 1;
    }
  }
  return quarters;
}

/** The signature names, read from the phase history: who submitted, endorsed, approved. */
export function signatureNames(history: TransitionEntry[]): {
  preparedBy: string | null;
  endorsedBy: string | null;
  approvedBy: string | null;
} {
  const actorOf = (event: string): string | null =>
    history.find((entry) => entry.event.toUpperCase() === event)?.actorDisplayName ?? null;
  return {
    preparedBy: actorOf("SUBMIT"),
    endorsedBy: actorOf("ENDORSE"),
    approvedBy: actorOf("APPROVE"),
  };
}

/** The years the sheet can show: the schedule's own year plus every activity year. */
export function candidateYears(scheduleYear: number, activities: Activity[]): number[] {
  const years = new Set<number>([scheduleYear]);
  for (const activity of activities) {
    const year = Number(activity.plannedDate.slice(0, 4));
    if (Number.isInteger(year)) {
      years.add(year);
    }
  }
  return [...years].sort((a, b) => a - b);
}

/** First day of month <= period end and last day of month >= period start. */
function periodTouchesMonth(period: CalendarPeriod, year: number, month: number): boolean {
  const monthStart = `${String(year)}-${String(month).padStart(2, "0")}-01`;
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const monthEndExclusive = `${String(nextYear)}-${String(nextMonth).padStart(2, "0")}-01`;
  return period.startsOn < monthEndExclusive && period.endsOn >= monthStart;
}

export interface YearGridProps {
  readonly year: number;
  readonly activities: Activity[];
  /** Wave W2: the RFIs in the same year, square marks on their contractor's row. */
  readonly rfis?: readonly RfiSummary[];
  readonly periods: CalendarPeriod[];
  readonly showShutdown: boolean;
  readonly showRamadan: boolean;
}

/** One sheet. Pure rendering; the toggles and the year selection live in the parent. */
export function YearGrid({
  year,
  activities,
  rfis = [],
  periods,
  showShutdown,
  showRamadan,
}: YearGridProps): React.JSX.Element {
  const yearActivities = activities.filter((entry) => entry.plannedDate.startsWith(String(year)));
  const yearRfis = rfis.filter((entry) => entry.plannedDate.startsWith(String(year)));
  const contractors: string[] = [];
  for (const entry of [...yearActivities, ...yearRfis]) {
    if (!contractors.includes(entry.contractorPartyName)) {
      contractors.push(entry.contractorPartyName);
    }
  }
  const shutdownPeriods = showShutdown ? periods.filter((entry) => entry.kind === "SHUTDOWN") : [];
  const ramadanPeriods = showRamadan ? periods.filter((entry) => entry.kind === "RAMADAN") : [];
  const counts = quarterCounts(yearActivities);

  // Wave W2: ONE scroll container in BOTH axes, with a height of its own, so every
  // header holds while the sheet scrolls either way: the month row (top), the contractor
  // column (left), the corner, and the quarterly rollup (bottom). The old wrapper
  // scrolled sideways only, which made the top-sticky header inert.
  return (
    <div className="q-yeargrid-viewport" data-testid="year-grid">
      {contractors.length === 0 && <p className="q-note">{strings.schedules.yearGridEmpty}</p>}
      {contractors.length > 0 && (
        <table className="q-register q-yeargrid">
          <caption className="q-register__caption">{strings.schedules.yearGridHeading}</caption>
          <thead>
            <tr>
              <th scope="col" className="q-yeargrid__contractor">
                {strings.schedules.columnContractor}
              </th>
              {MONTH_NAMES.map((name) => (
                <th scope="col" key={name}>
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contractors.map((contractor) => (
              <tr key={contractor}>
                <th scope="row" className="q-yeargrid__contractor">
                  {contractor}
                </th>
                {MONTH_NAMES.map((name, index) => {
                  const month = index + 1;
                  const overlays: string[] = [];
                  if (shutdownPeriods.some((entry) => periodTouchesMonth(entry, year, month))) {
                    overlays.push("q-yeargrid__shutdown");
                  }
                  if (ramadanPeriods.some((entry) => periodTouchesMonth(entry, year, month))) {
                    overlays.push("q-yeargrid__ramadan");
                  }
                  const dots = yearActivities.filter(
                    (entry) =>
                      entry.contractorPartyName === contractor &&
                      Number(entry.plannedDate.slice(5, 7)) === month,
                  );
                  const squares = yearRfis.filter(
                    (entry) =>
                      entry.contractorPartyName === contractor &&
                      Number(entry.plannedDate.slice(5, 7)) === month,
                  );
                  return (
                    <td
                      key={name}
                      className={overlays.join(" ")}
                      data-testid={`month-${String(year)}-${String(month).padStart(2, "0")}`}
                    >
                      {dots.map((entry) => (
                        <Link
                          key={entry.id}
                          to={`/activities/${entry.id}`}
                          className={`q-yeargrid__dot q-yeargrid__dot--${activityAttention(entry)}`}
                          title={`${entry.humanId} ${entry.verificationTypeLabel} · ${entry.plannedDate} · ${strings.schedules.negotiation[entry.negotiationState] ?? term(entry.status)}`}
                        >
                          <span className="q-visually-hidden">
                            {entry.humanId} {entry.verificationTypeLabel} {entry.plannedDate}
                          </span>
                        </Link>
                      ))}
                      {squares.map((entry) => (
                        <Link
                          key={entry.id}
                          to={`/rfis/${entry.id}`}
                          className={`q-yeargrid__dot q-yeargrid__dot--rfi q-yeargrid__dot--${rfiAttention(entry)}`}
                          title={`${entry.humanId} ${entry.scope} · ${entry.plannedDate} · ${strings.rfis.dateState[entry.dateState] ?? entry.dateState}`}
                        >
                          <span className="q-visually-hidden">
                            {entry.humanId} {entry.scope} {entry.plannedDate}
                          </span>
                        </Link>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot data-testid="quarterly-rollup">
            <tr>
              <th scope="row" colSpan={13} className="q-yeargrid__rollup-heading">
                {strings.schedules.rollupHeading}
              </th>
            </tr>
            <tr>
              <td className="q-yeargrid__contractor"></td>
              {[1, 2, 3, 4].map((quarter) => (
                <td key={quarter} colSpan={3}>
                  <strong>{strings.schedules.quarterLabel(quarter)}</strong>
                  {ROLLUP_STATUSES.map((status) => (
                    <div key={status} data-testid={`rollup-${String(quarter)}-${status}`}>
                      {ROLLUP_LABELS[status]}: {counts[quarter]?.[status] ?? 0}
                    </div>
                  ))}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

export interface SignatureBlockProps {
  readonly history: TransitionEntry[];
}

/** The signature block row: prepared / endorsed / approved, names from the phase history. */
export function SignatureBlock({ history }: SignatureBlockProps): React.JSX.Element {
  const names = signatureNames(history);
  const rows: [string, string | null][] = [
    [strings.schedules.preparedBy, names.preparedBy],
    [strings.schedules.endorsedBy, names.endorsedBy],
    [strings.schedules.approvedBy, names.approvedBy],
  ];
  return (
    <table className="q-register" data-testid="signature-block">
      <caption className="q-register__caption">{strings.schedules.signatureHeading}</caption>
      <tbody>
        {rows.map(([label, name]) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            <td>{name ?? strings.schedules.signatureNobody}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
